import './styling/legacy-styles.css';
import './annotation_renderer.scss';
import sum from 'hash-sum';
import { h, render } from 'preact';
import { ANNOTATION_EVENT, PLAYER_EVENT, APPLICATION_EVENT } from 'constants/events';
import { unique, notEmpty } from 'utilities/list';
import { eventHandlers } from 'utilities/parsers';
import { LayoutEngine } from 'engine/layout/layout_engine';
import { Targets } from './targets';

export class AnnotationRenderer {
    constructor(options, eventBus) {
        this.options = options;
        this.eventBus = eventBus;
        this.annotationList = [];
        this.layoutEngine = new LayoutEngine(options.data.display);
        this.eventBus.on(APPLICATION_EVENT.UPDATED, this.onApplicationUpdated);
        this.eventBus.on(PLAYER_EVENT.LOADED_METADATA, this.onPlayerLoadedMetadata);
        this.eventBus.on(ANNOTATION_EVENT.ANNOTATIONS_UPDATED, this.onAnnotationsUpdated);
    }

    onAnnotationsUpdated = list => {
        this.annotationList = list;
        this.layoutEngine.activeRegions = this.renderTargets;
        this.render();
    }

    onApplicationUpdated = updates => {
        if (this.shouldUpdate(updates)) {
            this.options.data.display = updates.display;
            this.options.data.state = updates.state;
            this.options.data.project = updates.project;
            this.layoutEngine.activeLayout = updates.display.layout;
            this.render();
        }
    }

    onPlayerLoadedMetadata = (event, player, retry = 0) => {
        // wait one event loop to increase likelihood that 
        // video dimensions are accurate - IE/Edge safeguard
        requestAnimationFrame(() => {
            if (player && player.dimensions && player.dimensions.video) {
                const { width, height } = player.dimensions.video;
                this.layoutEngine.activePlayerAspect = height / width * 100;
                this.render();
            } else if (retry < 3) {
                this.onPlayerLoadedMetadata(event, player, retry + 1);
            }
        });
    }

    render() {
        render(<Targets { ...this.displaySettings }/>, this.options.container);
    }

    destroy() {
        try {
            render(null, this.options.container);
        } catch(e) {}
    }

    get renderTargets() {
        return this.annotationList.map(a => a.target).filter(unique).filter(notEmpty);
    }

    get displaySettings() {
        const { display } = this.options.data;

        return {
            display,
            data: this.options.data,
            bus: this.eventBus,
            list: this.annotationList,
            animate: display.animate,
            targetList: this.renderTargets,
            autoplay: this.options.autoplay,
            loop: this.options.loop,
            muted: this.options.muted,
            controls: this.options.controls,
            captions: this.options.captions,
            contentDimensions: this.layoutEngine.contentDimensions,
            playerInterface: this.options.playerAdapter,
            eventHandlers: eventHandlers(this.options),
            scaleToContainer: this.options.scaleToContainer
        }
    }

    get rendered() {
        return this.options.container && this.options.container.firstChild;
    }

    shouldUpdate({ display, state, project }) {
        return (
            sum(this.options.data.display) !== sum(display) ||
            sum(this.options.data.project) !== sum(project) ||
            sum(this.options.data.state) !== sum(state)
        )
    }
}